/*
  Peter Augustus homepage hero | 13 Oct 2026 | Theme 138814587088

  Registers "hero" with PAHome (assets/pa-home-gsap.js). Loads after
  pa-vendor-splittext.min.js and pa-vendor-customease.min.js.

  - Slides: the active line fills over "Time per slide" and moves to the next
    slide when full (Osmo Tab System with Autoplay: the progress bar is the timer).
    Clicking a line jumps to its slide. Only the active slide's video plays.
  - Heading: rises line by line out of a mask, body and button follow
    (Osmo Masked Text Reveal, lines 0.8s, stagger 0.08, Osmo's ease).
  - Video: drifts as the page scrolls (Osmo Global Parallax), desktop only.
  - Pauses while the hero is off screen, when the pause button is pressed, and
    on the slide picked in the theme editor. Reduced motion: no autoplay, no
    drift, videos start paused, slides change instantly.
  - Desktop header over the hero: turns solid once the hero has scrolled under it.
*/
(() => {
  if (!window.PAHome || !window.gsap) return;

  if (window.SplitText) gsap.registerPlugin(window.SplitText);
  let EASE = 'power3.inOut';
  if (window.CustomEase) {
    gsap.registerPlugin(window.CustomEase);
    window.CustomEase.create('osmo-ease', '0.625, 0.05, 0, 1');
    EASE = 'osmo-ease';
  }

  window.PAHome.register('hero', (hero, { isMobile, navDesktop, reduceMotion, designMode }, context) => {
    const slides = [...hero.querySelectorAll('[data-slide]')];
    if (!slides.length) return undefined;

    const lines = [...hero.querySelectorAll('[data-hero-line]')];
    const pauseButton = hero.querySelector('[data-hero-pause]');
    const header = document.querySelector('.pa-header');
    const t = (seconds) => (reduceMotion ? 0 : seconds);
    const duration = Math.max(3, Number(hero.dataset.duration) || 8);
    const autoplay = hero.dataset.autoplay === 'true' && slides.length > 1 && !reduceMotion;

    const offs = [];
    const on = (target, type, fn) => {
      target.addEventListener(type, fn);
      offs.push(() => target.removeEventListener(type, fn));
    };

    let index = 0;
    let timer = null;
    let userPaused = reduceMotion; // reduced motion starts with the video still
    let inView = true;
    let editorHold = false;
    let alive = true;

    // The video for this layout: the mobile clip when there is one, otherwise the main one
    const videoOf = (slide) => (isMobile && slide.querySelector('.pa-hero__video--mobile')) || slide.querySelector('.pa-hero__video--desktop') || slide.querySelector('video');
    const play = (slide) => {
      const video = videoOf(slide);
      if (!video) return;
      video.muted = true;
      if (video.preload === 'none') video.preload = 'auto';
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };
    const stop = (slide) => slide.querySelectorAll('video').forEach((video) => video.pause());
    const fillOf = (i) => (lines[i] ? lines[i].querySelector('.pa-hero__line-fill') : null);

    function sync() {
      const held = userPaused || !inView || editorHold;
      if (timer) {
        if (held) timer.pause();
        else timer.resume();
      }
      if (userPaused || !inView) stop(slides[index]);
      else play(slides[index]);
      if (pauseButton) {
        pauseButton.setAttribute('aria-pressed', String(userPaused));
        pauseButton.setAttribute('aria-label', userPaused ? 'Play' : 'Pause');
      }
    }

    // Osmo Masked Text Reveal on the heading, then the eyebrow, body and button fade up.
    // A split is reverted once its lines are up, so the heading reflows normally on resize.
    const splits = new Map();
    context.add('reveal', (slide) => {
      const content = slide.querySelector('.pa-hero__content');
      const heading = slide.querySelector('[data-hero-heading]');
      const fades = [...slide.querySelectorAll('[data-hero-fade]')];
      gsap.set(content, { opacity: 1 });
      if (reduceMotion) {
        gsap.set([heading, ...fades].filter(Boolean), { autoAlpha: 1, clearProps: 'transform' });
        return;
      }
      const tl = gsap.timeline();
      if (heading) {
        gsap.set(heading, { autoAlpha: 1 });
        if (splits.has(heading)) splits.get(heading).revert(); // a quick click back to a slide still mid-reveal
        if (window.SplitText) {
          const split = window.SplitText.create(heading, { type: 'lines', mask: 'lines' });
          splits.set(heading, split);
          tl.fromTo(split.lines, { yPercent: 110 }, {
            yPercent: 0,
            duration: 0.8,
            stagger: 0.08,
            ease: EASE,
            onComplete: () => {
              split.revert();
              splits.delete(heading);
            }
          }, 0);
        }
      }
      if (fades.length) tl.fromTo(fades, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.08, ease: 'power3.out' }, 0.25);
    });

    function startTimer() {
      if (timer) timer.kill();
      timer = null;
      lines.forEach((line, i) => {
        line.setAttribute('aria-current', String(i === index));
        gsap.set(fillOf(i), { scaleX: i === index && !autoplay ? 1 : 0 });
      });
      if (!autoplay) return;
      timer = gsap.fromTo(fillOf(index), { scaleX: 0 }, {
        scaleX: 1,
        duration,
        ease: 'none',
        onComplete: () => context.show((index + 1) % slides.length)
      });
      if (userPaused || !inView || editorHold) timer.pause();
    }

    context.add('show', (next, first) => {
      if (!alive || (next === index && !first)) return;
      const previous = slides[index];
      const incoming = slides[next];
      index = next;

      slides.forEach((slide, i) => {
        const active = i === next;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });

      if (!first && previous !== incoming) {
        previous.classList.add('is-leaving');
        gsap.to(previous.querySelectorAll('[data-hero-heading], [data-hero-fade]'), { autoAlpha: 0, duration: t(0.35), ease: 'power2.in' });
        gsap.fromTo(incoming.querySelector('[data-hero-media]'), { autoAlpha: 0 }, {
          autoAlpha: 1,
          duration: t(1),
          ease: 'power2.inOut',
          onComplete: () => {
            previous.classList.remove('is-leaving');
            stop(previous);
          }
        });
      }

      if (!userPaused && inView) play(incoming);
      context.reveal(incoming);
      startTimer();
    });

    // A Slide block cannot know its place in the Slides group, so the first slide is
    // marked active, the slides labelled and the first video started here (until now
    // CSS showed the first slide). The video plays behind the preloader, as autoplay did.
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === 0);
      slide.setAttribute('aria-hidden', String(i !== 0));
      slide.inert = i !== 0;
      if (slides.length > 1) {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
      }
    });
    hero.classList.add('is-ready');
    if (pauseButton && slides.length < 2 && !hero.querySelector('video')) pauseButton.hidden = true;
    if (!userPaused) play(slides[0]);

    // Wait for Aviano, or SplitText measures the lines with the fallback font, and for
    // the preloader (sections/pa-preloader.liquid) to open, or the reveal plays unseen behind it
    Promise.all([
      document.fonts ? document.fonts.ready : null,
      window.PAPreloader ? window.PAPreloader.done : null
    ]).then(() => {
      if (alive) context.show(0, true);
    });

    lines.forEach((line, i) => on(line, 'click', () => context.show(i)));

    if (pauseButton) {
      on(pauseButton, 'click', () => {
        userPaused = !userPaused;
        sync();
      });
    }
    sync(); // reduced motion: stops the first video's autoplay and labels the button Play

    // Stop the timer and the video while the hero is off screen
    ScrollTrigger.create({
      trigger: hero,
      start: 'top bottom',
      end: 'bottom top',
      onToggle: (self) => {
        inView = self.isActive;
        sync();
      }
    });

    // Osmo Global Parallax: the media drifts down as the page scrolls, inside its frame
    if (!isMobile && !reduceMotion) {
      hero.querySelectorAll('[data-hero-drift]').forEach((media) => {
        gsap.fromTo(media, { yPercent: 0 }, {
          yPercent: 13, // 15% of the frame, the extra height the media has in CSS
          ease: 'none',
          scrollTrigger: { trigger: hero, start: 'clamp(top top)', end: 'clamp(bottom top)', scrub: true }
        });
      });
    }

    // Desktop header over the hero turns solid once the hero is under it
    const firstSection = document.querySelector('#main-content > .shopify-section');
    if (navDesktop && header && hero.hasAttribute('data-pa-header-overlay') && firstSection && firstSection.contains(hero)) {
      const bar = header.querySelector('.pa-header__bar');
      // Enter and leave-back only: toggleClass with end 'max' dropped the class at the bottom of short pages
      ScrollTrigger.create({
        trigger: hero,
        start: () => `bottom top+=${bar ? bar.offsetHeight : 0}`,
        onEnter: () => header.classList.add('is-solid'),
        onLeaveBack: () => header.classList.remove('is-solid')
      });
    }

    // Theme editor: picking a Slide block shows it and holds it there. Slides sit
    // inside the Slides group, so they are matched by block id as well as by target.
    if (designMode) {
      const slideFor = (event) => {
        const id = event.detail && event.detail.blockId;
        const i = slides.findIndex((slide) => slide.dataset.blockId === id);
        return i >= 0 ? i : slides.indexOf(event.target);
      };
      on(document, 'shopify:block:select', (event) => {
        const i = slideFor(event);
        if (i < 0) return;
        editorHold = true;
        context.show(i);
        sync();
      });
      on(document, 'shopify:block:deselect', (event) => {
        if (slideFor(event) < 0 && !hero.contains(event.target)) return;
        editorHold = false;
        sync();
      });
    }

    return () => {
      alive = false;
      offs.forEach((off) => off());
      if (header) header.classList.remove('is-solid');
      slides.forEach(stop);
    };
  });
})();
